import React, { ReactNode } from 'react';

interface ModalProps {
    title: string;
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    footerButtons: ReactNode[];
}

const ZoomModal: React.FC<ModalProps> = ({
    title,
    isOpen,
    onClose,
    children,
    footerButtons,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 w-screen">
            <div className="relative bg-white w-screen h-screen overflow-hidden flex flex-col">
                <div className="flex-grow">
                    <div className="w-full h-full">{children}</div>
                </div>

                {/* Footer with buttons */}
                <div className="p-4 border-t flex justify-center items-center">
                    {footerButtons.map((button, index) => (
                        <div key={index} className="">
                            {button}
                        </div>
                    ))}
                </div>
                <div className="flex justify-between items-center p-4 fixed top-0 right-[0]">
                    <button
                        onClick={onClose}
                        className="focus:outline-none"
                        data-testid="close-zoom-in-modal-button"
                    >
                        <svg
                            width="34"
                            height="36"
                            viewBox="0 0 34 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M17 36C8.98612 36 4.97918 36 2.4896 33.3639C-2.02656e-07 30.728 0 26.4852 0 18C0 9.51471 -2.02656e-07 5.27207 2.4896 2.63605C4.97918 -2.14577e-07 8.98612 0 17 0C25.0138 0 29.0209 -2.14577e-07 31.5103 2.63605C34 5.27207 34 9.51471 34 18C34 26.4852 34 30.728 31.5103 33.3639C29.0209 36 25.0138 36 17 36Z"
                                fill="white"
                                fillOpacity="0.5"
                            />
                            <path
                                d="M17 35.85C12.9886 35.85 9.99597 35.8496 7.69433 35.522C5.39792 35.1951 3.81134 34.545 2.59865 33.2609C1.38481 31.9757 0.769284 30.2922 0.460005 27.8565C0.150285 25.4174 0.15 22.2466 0.15 18C0.15 13.7533 0.150285 10.5826 0.460005 8.14345C0.769284 5.70775 1.38481 4.02428 2.59865 2.73904L2.4896 2.63605L2.59865 2.73904C3.81134 1.455 5.39792 0.804915 7.69433 0.478009C9.99597 0.150356 12.9886 0.15 17 0.15C21.0114 0.15 24.004 0.150356 26.3057 0.478009C28.6021 0.804916 30.1887 1.455 31.4013 2.73904L31.5103 2.63605L31.4013 2.73904C32.6152 4.02428 33.2307 5.70775 33.54 8.14345C33.8497 10.5826 33.85 13.7533 33.85 18C33.85 22.2466 33.8497 25.4174 33.54 27.8565C33.2307 30.2922 32.6152 31.9757 31.4013 33.2609L31.5103 33.3639L31.4013 33.2609C30.1887 34.545 28.6021 35.1951 26.3057 35.522C24.004 35.8496 21.0114 35.85 17 35.85Z"
                                stroke="white"
                                strokeOpacity="0.6"
                                strokeWidth="0.3"
                            />
                            <g opacity="0.5">
                                <path
                                    d="M16.9948 33.6615C9.13804 33.6615 5.20967 33.6615 2.76891 31.2206C0.328125 28.78 0.328125 24.8515 0.328125 16.9948C0.328125 9.13804 0.328125 5.20967 2.76891 2.76891C5.20967 0.328125 9.13804 0.328125 16.9948 0.328125C24.8515 0.328125 28.78 0.328125 31.2206 2.76891C33.6615 5.20967 33.6615 9.13804 33.6615 16.9948C33.6615 24.8515 33.6615 28.78 31.2206 31.2206C28.78 33.6615 24.8515 33.6615 16.9948 33.6615Z"
                                    fill="url(#paint0_linear_4084_34032)"
                                    fillOpacity="0.5"
                                />
                                <path
                                    d="M3.12247 30.8671L3.12245 30.8671C2.01046 29.7551 1.42641 28.2859 1.12876 26.0721C0.829187 23.8438 0.828125 20.9373 0.828125 16.9948C0.828125 13.0523 0.829187 10.1457 1.12876 7.91748C1.42641 5.70364 2.01045 4.23446 3.12246 3.12246L3.12246 3.12246C4.23446 2.01045 5.70364 1.42641 7.91748 1.12876C10.1457 0.829187 13.0523 0.828125 16.9948 0.828125C20.9373 0.828125 23.8438 0.829187 26.0721 1.12876C28.2859 1.42641 29.7551 2.01046 30.8671 3.12245L30.8671 3.12247C31.9791 4.23446 32.5632 5.70365 32.8608 7.91748C33.1604 10.1457 33.1615 13.0523 33.1615 16.9948C33.1615 20.9373 33.1604 23.8438 32.8608 26.0721C32.5632 28.2859 31.9791 29.7551 30.8671 30.8671L30.8671 30.8671C29.7551 31.9791 28.2859 32.5632 26.0721 32.8608C23.8438 33.1604 20.9373 33.1615 16.9948 33.1615C13.0523 33.1615 10.1457 33.1604 7.91748 32.8608C5.70365 32.5632 4.23446 31.9791 3.12247 30.8671Z"
                                    stroke="#8E9BBA"
                                    strokeOpacity="0.5"
                                />
                            </g>
                            <path
                                d="M11.9442 11.9442C12.4324 11.4561 13.2239 11.4561 13.712 11.9442L16.9948 15.2271L20.2776 11.9443C20.7658 11.4561 21.5571 11.4561 22.0453 11.9443C22.5335 12.4324 22.5335 13.2239 22.0453 13.712L18.7626 16.9948L22.0453 20.2776C22.5335 20.7656 22.5335 21.5571 22.0453 22.0453C21.5571 22.5335 20.7656 22.5335 20.2775 22.0453L16.9948 18.7626L13.7121 22.0453C13.2239 22.5335 12.4324 22.5335 11.9443 22.0453C11.4561 21.5571 11.4561 20.7658 11.9443 20.2776L15.2271 16.9948L11.9442 13.712C11.4561 13.2239 11.4561 12.4324 11.9442 11.9442Z"
                                fill="url(#paint1_linear_4084_34032)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_4084_34032"
                                    x1="16.9948"
                                    y1="5.32812"
                                    x2="16.9948"
                                    y2="23.3281"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop offset="0.03" stop-color="#9FCBF2" />
                                    <stop offset="0.645" stop-color="#7DBAF0" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_4084_34032"
                                    x1="11.5781"
                                    y1="16.9948"
                                    x2="22.4114"
                                    y2="16.9948"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#527FED" />
                                    <stop offset="1" stop-color="#275BDD" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ZoomModal;
